import { QueryKey } from '@api'
import { useApi } from '@api/useApi.hook'
import type { ApiGETFn } from '@api/standaloneApi'
import { useQuery } from '@tanstack/react-query-v4'
import { useAuth, useIsAuthenticated } from '@domain/auth/Auth.context'
import { appConfig } from '@config'
import { User } from '@domain/users'
import _get from 'lodash/get'
import { usePrivy } from '@privy-io/react-auth'

export const useMe = ({
  refetchOnWindowFocus,
}: {
  refetchOnWindowFocus?: boolean | 'always'
} = {}) => {
  const isAuthenticated = useIsAuthenticated()
  const { authenticated } = usePrivy()
  const [auth] = useAuth()

  const { get } = useApi()

  const userUuid = _get(auth, 'user.uuid', '')

  const avatarUrl = appConfig.endpoints.v1.users.avatarBucketUrl

  return useQuery({
    queryKey: [QueryKey.Me, isAuthenticated, userUuid, authenticated],
    queryFn: async () => {
      try {
        if (isAuthenticated && authenticated) {
          const result = await (get as ApiGETFn)<User>(appConfig.endpoints.v1.me.main)()
          return result
        }
        return {} as User
      } catch (error) {
        console.error(error)
        return {} as User
      }
    },
    select: (me) => {
      const bucket = avatarUrl?.replace(/\/$/, '') ?? ''
      const avatarPath = me?.avatar?.trim()
      const avatarUrlFallback = bucket ? `${bucket}/default-avatar.png` : ''
      const resolvedAvatarUrl =
        avatarPath && bucket ? `${bucket}/${avatarPath}` : avatarUrlFallback

      return {
        ...me,
        avatarUrl: resolvedAvatarUrl,
        avatarUrlFallback,
      }
    },
    refetchOnWindowFocus,
    ...appConfig.cache.me,
  })
}
